import { message, Modal } from "antd";
import cuid from "cuid";
import { defaults, find, get, keyBy } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { CREATE_LINK } from "utils/api/graphql/mutations/link";
import { UPDATE_PROJECT } from "utils/api/graphql/mutations/projects";
import { TRACER } from "utils/api/graphql/queries/tracers";
import {
  CONTACT_POINT_SYSTEM,
  MODAL_STATES,
  PROCESS_TYPES,
  TEMPLATES,
  THEMES_PALETTE,
} from "utils/constants";
import Done from "./Done";
import Initial from "./Initial";
import Pending from "./Pending";

const DownloadOrSend = ({
  data,
  open,
  onClose,
  onSendQuote,
  onDownload,
  fields,
  insurancePlans,
  project,
}) => {
  const { id } = useParams();
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const [state, setState] = useState(MODAL_STATES.INITIAL);
  const [addLinks, setAddLinks] = useState(true);
  const [selectedData, setSelectedData] = useState([]);
  const [addLink] = useMutation(CREATE_LINK);
  const [getTracer] = useLazyQuery(TRACER);
  const [updateProject] = useMutation(UPDATE_PROJECT);
  const [justifications, setJustifications] = useState(
    get(
      fields,
      "justifications",
      data.reduce((acc, { insurancePlan: { id } }) => {
        acc[id] = "";
        return acc;
      }, {}),
    ),
  );

  const telecoms = defaults(keyBy(get(project, "contact.telecoms"), "system"), {
    [CONTACT_POINT_SYSTEM.EMAIL]: { value: null },
    [CONTACT_POINT_SYSTEM.PHONE]: { value: null },
  });

  const initialValues = {
    contact: {
      ...get(project, "contact", {}),
      user: {
        ...get(project, "contact.user", {}),
        firstname: get(project, "contact.user.firstname", ""),
        lastname: get(project, "contact.user.lastname", ""),
      },
      telecoms,
    },
  };
  const onCheck = (id, state) => {
    if (state) return setSelectedData((prev) => [...prev, id]);
    setSelectedData((prev) => [...prev.filter((item) => item !== id)]);
    onJustify(undefined, id);
  };

  const onJustify = (justification, insuranceId) =>
    setJustifications((prev) => ({
      ...prev,
      [insuranceId]: justification,
    }));

  const onSubmit = (values) => {
    const telecomsArray = Object.entries(values.contact.telecoms || {})
      .map(([system, telecom]) => ({
        id: telecom.id || cuid(),
        system,
        value: telecom.value,
      }))
      .filter((telecom) => telecom.value);

    return new Promise((resolve, reject) => {
      updateProject({
        variables: {
          where: { id: project.id },
          data: {
            contact: {
              ...values.contact,
              user: values.contact.user,
              telecoms: telecomsArray,
            },
          },
        },
        onCompleted: () => {
          resolve();
        },
        onError: (error) => {
          console.error("Error saving contact:", error);
          message.error(
            "Failed to save contact information. Please try again.",
          );
          reject(error);
        },
      });
    });
  };

  const sendQuote = (submitForm) => {
    if (submitForm) {
      submitForm().then(() => {
        onSendQuote({
          selectedData,
          payload: {
            fields: {
              ...fields,
              insurancePlans: [
                ...get(fields, "insurancePlans", []),
                ...selectedData,
              ],
              lastSelected: selectedData,
              addLinks,
              justifications,
            },
          },
        });
        onClose();
      });
    } else {
      onSendQuote({
        selectedData,
        payload: {
          fields: {
            ...fields,
            insurancePlans: [
              ...get(fields, "insurancePlans", []),
              ...selectedData,
            ],
            lastSelected: selectedData,
            addLinks,
            justifications,
          },
        },
      });
      onClose();
    }
  };

  const download = (submitForm) => {
    if (submitForm) {
      submitForm().then(() => {
        onDownload({
          setState,
          setSelectedData,
          selectedData,
          payload: {
            locked: true,
            fields: {
              ...fields,
              insurancePlans: [
                ...get(fields, "insurancePlans", []),
                ...selectedData,
              ],
              justifications,
            },
          },
        });
      });
    } else {
      onDownload({
        setState,
        setSelectedData,
        selectedData,
        payload: {
          locked: true,
          fields: {
            ...fields,
            insurancePlans: [
              ...get(fields, "insurancePlans", []),
              ...selectedData,
            ],
            justifications,
          },
        },
      });
    }
  };

  const onCopyLink = () => {
    const template =
      TEMPLATES[PROCESS_TYPES.B2C].THEMES[
        THEMES_PALETTE.DEFAULT.PROSPECT_PROCESS
      ].PRODUCTS;
    getTracer({
      variables: {
        where: {
          flow: {
            process: {
              theme: THEMES_PALETTE.DEFAULT.PROSPECT_PROCESS,
            },
          },
        },
      },
      onCompleted: ({ tracer }) => {
        const statuses = get(tracer, "flow.process.processStatuses", []);
        const selectedStatus = find(statuses, { template })?.id;
        const url = `${
          process.env.REACT_APP_BASE_URL
        }/#/public/process/init/${get(tracer, "source.id")}`;
        addLink({
          variables: {
            data: {
              url,
              project: { id },
              fields: {
                process: { id: get(tracer, "flow.process.id") },
                status: { id: selectedStatus },
              },
            },
          },
          onCompleted: ({ addLink }) => {
            navigator.clipboard.writeText(`${url}?l=${addLink.id}`);
            message.success(
              DETAIL.TEMPLATES.COMPARISON.DOWNLOAD_OR_SEND.COPIED,
            );
          },
        });
      },
    });
  };

  const MODAL_STATE = {
    [MODAL_STATES.INITIAL]: (
      <Initial
        data={data}
        onDownload={download}
        onCheck={onCheck}
        onJustify={onJustify}
        justifications={justifications}
        selectedData={selectedData}
        insurancePlans={insurancePlans}
        translation={DETAIL.TEMPLATES.COMPARISON.DOWNLOAD_OR_SEND}
        initialValues={initialValues}
        onSubmit={onSubmit}
        onSendQuote={sendQuote}
        setAddLinks={setAddLinks}
        addLinks={addLinks}
      />
    ),
    [MODAL_STATES.PENDING]: (
      <Pending translation={DETAIL.TEMPLATES.COMPARISON.DOWNLOAD_OR_SEND} />
    ),
    [MODAL_STATES.DONE]: (
      <Done translation={DETAIL.TEMPLATES.COMPARISON.DOWNLOAD_OR_SEND} />
    ),
  };

  return (
    <Modal open={open} onCancel={onClose} width={"60%"} footer={null}>
      {MODAL_STATE[state]}
    </Modal>
  );
};

export default DownloadOrSend;
